'use strict';

import { CONFIG } from '../core/config.js';

/**
 * DOM read/write helpers for the Search Filter Form URL layer.
 *
 * These functions read form selections/datasets and mutate checkbox/radio inputs. They
 * do not compute URL/param values, read `window`/`history`, schedule
 * `requestAnimationFrame`, or fire sync callbacks — that orchestration stays in
 * `managers/url.js` (URLManager). Pure value logic lives in `utils/url-logic.js`.
 */

const TABBED_FIELDSET_SELECTOR = '[data-search-filter-form-tabbed]';
const TABBED_SELECTED_CLASS = 'search-filter-form-tabbed__option--selected';
const TYPE_OF_CARE_KEY = 'type-of-care';

/**
 * Read the form submission URL attribute from a form.
 *
 * @param {HTMLFormElement} form - The form element.
 * @return {string|undefined} The raw attribute value.
 */
export function readFormSubmissionUrlAttribute(form) {
	return form.dataset.searchFilterFormSubmissionUrl;
}

/**
 * Read whether the form opts to omit state/insurance path segments.
 *
 * @param {HTMLFormElement} form - The form element.
 * @return {boolean} True when the path should be omitted.
 */
export function readOmitStateInsurancePath(form) {
	return form.dataset.searchFilterFormOmitStateInsurancePath === 'true';
}

/**
 * Collect the checked checkbox/radio selections from a form.
 *
 * @param {HTMLFormElement} form - The form to read selections from.
 * @return {Object<string, string[]>} Map of input name → array of selected values.
 */
export function readSelections(form) {
	const checkboxes = form.querySelectorAll('input[type="checkbox"]:checked');
	const radios = form.querySelectorAll('input[type="radio"]:checked');

	const selections = {};

	for (const checkbox of checkboxes) {
		const name = checkbox.name;
		if (!selections[name]) {
			selections[name] = [];
		}
		selections[name].push(checkbox.value);
	}

	for (const radio of radios) {
		const name = radio.name;
		if (!selections[name]) {
			selections[name] = [];
		}
		selections[name].push(radio.value);
	}

	return selections;
}

/**
 * Read the slug for a selected input from its `data-search-data`, falling back to
 * slugifying the provided value.
 *
 * @param {string}   inputName     - The name attribute of the input (e.g., 'states-options').
 * @param {string}   fallbackValue - The value to slugify if no slug is found.
 * @param {Function} slugify       - Slugify function used for the fallback.
 * @return {string} The slug to use in the URL.
 */
export function readSlugFromSelection(inputName, fallbackValue, slugify) {
	const selectedCheckbox = document.querySelector(
		`input[name="${inputName}"]:checked`,
	);
	if (!selectedCheckbox?.dataset.searchData) {
		return slugify(fallbackValue);
	}

	try {
		const searchData = JSON.parse(selectedCheckbox.dataset.searchData);
		return searchData.slug || slugify(fallbackValue);
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn(`Error parsing search data for ${inputName} slug:`, error);
		return slugify(fallbackValue);
	}
}

/**
 * Find the checkboxes for a dropdown whose value is in the given set.
 *
 * @param {string}          dropdownType - The input name (e.g., 'states-options').
 * @param {string|string[]} values       - The value(s) to match.
 * @return {HTMLInputElement[]} The matching checkboxes.
 */
export function findMatchingCheckboxes(dropdownType, values) {
	const checkboxes = document.querySelectorAll(`input[name="${dropdownType}"]`);

	if (checkboxes.length === 0) return [];

	const valueSet = new Set(Array.isArray(values) ? values : [values]);
	const matching = [];

	for (const checkbox of checkboxes) {
		if (valueSet.has(checkbox.value)) {
			matching.push(checkbox);
		}
	}

	return matching;
}

/**
 * Check a checkbox and, for single-select inputs, mark its option as selected.
 *
 * @param {HTMLInputElement} checkbox - The checkbox to select.
 */
export function applyCheckboxSelection(checkbox) {
	checkbox.checked = true;

	if (checkbox.matches(CONFIG.ELEMENTS.checkboxSingleSelect)) {
		const option = checkbox.closest(CONFIG.ELEMENTS.option);
		if (option) {
			option.classList.add(CONFIG.CSS_CLASSES.optionSelected);
		}
	}
}

/**
 * Find the radios to select for a tabbed 'type-of-care' field — one per fieldset.
 *
 * @param {string} dropdownType - The input name (e.g., 'type-of-care-options').
 * @param {string} value        - The value to match.
 * @return {HTMLInputElement[]} The radios that should be selected.
 */
export function findRadiosToSelect(dropdownType, value) {
	const tabbedFields = document.querySelectorAll(TABBED_FIELDSET_SELECTOR);
	const radios = [];

	for (const fieldset of tabbedFields) {
		if (fieldset.dataset.searchFilterFormTabbed !== TYPE_OF_CARE_KEY) continue;

		const radioInputs = fieldset.querySelectorAll(
			`input[name="${dropdownType}"]`,
		);

		for (const radio of radioInputs) {
			if (radio.value === value) {
				radios.push(radio);
				break; // Only select one radio per fieldset
			}
		}
	}

	return radios;
}

/**
 * Check a radio and toggle the selected class on its tabbed labels.
 *
 * @param {HTMLInputElement} radio - The radio to select.
 * @return {boolean} True if the radio was inside a tabbed fieldset and updated.
 */
export function applyRadioSelection(radio) {
	radio.checked = true;

	const fieldset = radio.closest(TABBED_FIELDSET_SELECTOR);
	if (!fieldset) return false;

	const label = radio.closest('label');
	if (label) {
		label.classList.add(TABBED_SELECTED_CLASS);
	}

	// Remove selected class from other options in the same fieldset
	const otherLabels = fieldset.querySelectorAll(
		`label:not([for="${radio.id}"])`,
	);
	otherLabels.forEach((otherLabel) => {
		otherLabel.classList.remove(TABBED_SELECTED_CLASS);
	});

	return true;
}
